import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import ModuleLayout from '../components/ModuleLayout'
import Quiz from '../components/Quiz'
import { Key, Link2, CheckCircle } from 'lucide-react'

/* ─── Data ─── */
const KEY_TABLE = {
  headers: ['Offer ID', 'Date', 'Clicks'],
  rows: [
    ['offer_123', '2024-01-15', '1,250'],
    ['offer_123', '2024-01-16', '1,100'],
    ['offer_123', '2024-01-17', '1,300'],
    ['offer_456', '2024-01-15', '890'],
    ['offer_456', '2024-01-16', '950'],
    ['offer_456', '2024-01-17', '1,020'],
  ],
}

const CLICKS_TABLE = {
  headers: ['Date', 'Offer ID', 'Clicks'],
  rows: [
    ['2024-01-15', 'offer_123', '1,250'],
    ['2024-01-15', 'offer_456', '890'],
    ['2024-01-16', 'offer_123', '1,100'],
    ['2024-01-16', 'offer_456', '950'],
  ],
}

const CONV_TABLE = {
  headers: ['Date', 'Offer ID', 'Conversions'],
  rows: [
    ['2024-01-15', 'offer_123', '45'],
    ['2024-01-15', 'offer_456', '12'],
    ['2024-01-16', 'offer_123', '38'],
    ['2024-01-16', 'offer_456', '15'],
  ],
}

const QUIZ = [
  {
    question: 'What is a "key" in a table?',
    options: [
      'The first column in any table',
      'A column (or combination of columns) that uniquely identifies each row',
      'A password to access the spreadsheet',
      'The header row',
    ],
    correctIndex: 1,
    explanation: 'A key uniquely identifies each row. It\'s the answer to "which specific row are you talking about?" -- it could be one column or several columns together.',
  },
  {
    question: 'Looking at this table, which columns form the key?',
    context: {
      headers: ['Date', 'Offer ID', 'Clicks'],
      rows: [
        ['2024-01-15', 'offer_123', '1,250'],
        ['2024-01-15', 'offer_456', '890'],
        ['2024-01-16', 'offer_123', '1,100'],
        ['2024-01-16', 'offer_456', '950'],
      ],
    },
    options: [
      'Just the Date column',
      'Just the Offer ID column',
      'Offer ID + Date together',
      'The Clicks column',
    ],
    correctIndex: 2,
    explanation: 'Neither Offer ID nor Date alone is unique -- offer_123 appears on multiple dates, and each date has multiple offers. But Offer ID + Date together uniquely identifies exactly one row.',
  },
  {
    question: 'Why can\'t Offer ID alone be the key for daily data?',
    options: [
      'Offer IDs are too long',
      'Because the same Offer ID appears on multiple dates',
      'Offer IDs contain underscores',
      'Only numbers can be keys',
    ],
    correctIndex: 1,
    explanation: 'If offer_123 appears on Jan 15, Jan 16, and Jan 17, then "offer_123" alone matches 3 different rows. A key must point to exactly one row.',
  },
  {
    question: 'Two tables can be "joined" when they share...',
    options: [
      'The same number of rows',
      'The same file format',
      'The same key columns',
      'The same background color',
    ],
    correctIndex: 2,
    explanation: 'When two tables share the same key columns (like Offer ID + Date), you can match rows between them. That\'s a join -- it\'s how you combine clicks with conversions.',
  },
  {
    question: 'What is a composite key?',
    options: [
      'A key that uses only numbers',
      'A key made up of two or more columns together',
      'A key that is automatically generated',
      'A key column that contains formulas',
    ],
    correctIndex: 1,
    explanation: 'A composite key is when you need multiple columns together to uniquely identify a row. Offer ID + Date is a composite key because neither column is unique on its own.',
  },
  {
    question: 'Can the Clicks column be used as a key for this table?',
    context: {
      headers: ['Offer ID', 'Date', 'Clicks'],
      rows: [
        ['offer_123', '2024-01-15', '890'],
        ['offer_456', '2024-01-15', '890'],
        ['offer_123', '2024-01-16', '1,100'],
        ['offer_456', '2024-01-16', '950'],
      ],
    },
    options: [
      'Yes, any column can be a key',
      'No, because different rows can have the same click count -- it\'s not unique',
      'Only if the clicks are all different numbers',
      'Yes, but only if the column has no blank cells',
    ],
    correctIndex: 1,
    explanation: 'A key must uniquely identify each row. Two different offer/date combinations could have the same number of clicks (e.g., both had 890 clicks), so Clicks can\'t serve as a key. Keys should be identifiers, not data values.',
  },
  {
    question: 'You have a Clicks table and a Conversions table. Both have Date and Offer ID columns. What can you do with this?',
    options: [
      'Nothing -- two tables can\'t work together',
      'Join them by matching rows where Date and Offer ID are the same',
      'Copy and paste one table below the other',
      'Delete one table since they\'re duplicates',
    ],
    correctIndex: 1,
    explanation: 'Because both tables share the same key columns (Date + Offer ID), you can match each row of clicks with its corresponding conversions row. This join is how you build combined reports.',
  },
  {
    question: 'Why is a key like a home address?',
    options: [
      'Keys are always numbers, like house numbers',
      'Just the street name isn\'t enough -- you need the full address to find exactly one house',
      'Keys tell you which country the data is from',
      'Keys are optional, like apartment numbers',
    ],
    correctIndex: 1,
    explanation: 'A street name (like Offer ID) has many houses. A house number (like Date) exists on many streets. But the full address (Offer ID + Date) points to exactly one house (one specific row).',
  },
  {
    question: 'What happens if you try to join two tables but one has a row the other doesn\'t?',
    options: [
      'Both tables are deleted',
      'The unmatched row has no partner -- you\'ll get a missing value or the row is excluded from the result',
      'The spreadsheet creates fake data to fill the gap',
      'Nothing -- all joins always work perfectly',
    ],
    correctIndex: 1,
    explanation: 'If the Clicks table has offer_123 on Jan 17 but the Conversions table doesn\'t, that row has no match. Depending on the formula, you\'ll either get a blank, an #N/A error, or the row is left out of the combined result.',
  },
  {
    question: 'What is the composite key for this table?',
    context: {
      headers: ['Month', 'Region', 'Product', 'Revenue'],
      rows: [
        ['Jan', 'NSW', 'Home Loans', '$12,400'],
        ['Jan', 'NSW', 'Credit Cards', '$8,200'],
        ['Jan', 'VIC', 'Home Loans', '$9,800'],
        ['Feb', 'NSW', 'Home Loans', '$11,300'],
      ],
    },
    options: [
      'Just Revenue',
      'Month + Region + Product',
      'Just Month',
      'Region + Revenue',
    ],
    correctIndex: 1,
    explanation: 'Revenue is a data value, not an identifier. To uniquely identify each row, you need all three: which month, which region, and which product. Together, Month + Region + Product form the composite key.',
  },
  {
    question: 'What is the main benefit of having a key in your table?',
    options: [
      'It makes the table smaller',
      'It lets you look up the exact row you need without ambiguity',
      'It automatically sorts the table',
      'It prevents anyone from editing the data',
    ],
    correctIndex: 1,
    explanation: 'A key lets you pinpoint exactly one row. Without a key, a lookup might match multiple rows or the wrong row entirely. Keys are the foundation for reliable lookups, joins, and formulas.',
  },
  {
    question: 'The Clicks table has Date + Offer ID as its key. The Conversions table also has Date + Offer ID. What do we call this relationship?',
    options: [
      'A coincidence',
      'A shared key -- the basis for joining the two tables',
      'A conflict that needs to be resolved',
      'A formatting issue',
    ],
    correctIndex: 1,
    explanation: 'When two tables use the same columns as their key, those shared key columns create the link between them. This is what makes a join possible -- you match rows where the key values are identical.',
  },
]

const sectionVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
}

/* ─── Section 1: What is a Key? ─── */
function SectionWhatIsKey() {
  const [selectedCols, setSelectedCols] = useState([])
  const [feedback, setFeedback] = useState(null)

  const toggleCol = (colIdx) => {
    setFeedback(null)
    setSelectedCols((prev) =>
      prev.includes(colIdx) ? prev.filter((c) => c !== colIdx) : [...prev, colIdx]
    )
  }

  const checkKey = () => {
    // Correct answer: columns 0 (Offer ID) AND 1 (Date)
    const isCorrect =
      selectedCols.length === 2 && selectedCols.includes(0) && selectedCols.includes(1)
    const onlyOfferId = selectedCols.length === 1 && selectedCols.includes(0)
    const onlyDate = selectedCols.length === 1 && selectedCols.includes(1)

    if (isCorrect) {
      setFeedback({
        type: 'success',
        text: 'Correct! Offer ID + Date together uniquely identify each row. This is a composite key.',
      })
    } else if (onlyOfferId) {
      setFeedback({
        type: 'error',
        text: 'Not quite -- offer_123 appears on multiple dates (rows 1, 2, 3). Offer ID alone isn\'t unique. Try adding another column.',
      })
    } else if (onlyDate) {
      setFeedback({
        type: 'error',
        text: 'Not quite -- 2024-01-15 has multiple offers (rows 1 and 4). Date alone isn\'t unique. Try adding another column.',
      })
    } else if (selectedCols.includes(2)) {
      setFeedback({
        type: 'error',
        text: 'Clicks is a data value, not an identifier. Two different offer/date combos could have the same click count. Think about which columns identify WHAT the row is about.',
      })
    } else {
      setFeedback({
        type: 'error',
        text: 'Select the column(s) that, together, make each row unique. Hint: think about what combination you\'d need to find exactly one row.',
      })
    }
  }

  // Check if rows are unique for selected columns
  const getUniqueStatus = () => {
    if (selectedCols.length === 0) return null
    const seen = new Set()
    let hasDuplicate = false
    const duplicateRows = new Set()

    KEY_TABLE.rows.forEach((row, ri) => {
      const key = selectedCols.map((ci) => row[ci]).join('|')
      if (seen.has(key)) {
        hasDuplicate = true
        duplicateRows.add(ri)
        // Find the first occurrence too
        KEY_TABLE.rows.forEach((r2, ri2) => {
          if (ri2 < ri && selectedCols.map((ci) => r2[ci]).join('|') === key) {
            duplicateRows.add(ri2)
          }
        })
      }
      seen.add(key)
    })
    return { hasDuplicate, duplicateRows }
  }

  const uniqueStatus = getUniqueStatus()

  return (
    <motion.section
      variants={sectionVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.2 }}
      className="rounded-2xl border border-gray-200 bg-white p-6 sm:p-8 shadow-sm"
    >
      <h2 className="mb-2 text-2xl font-bold text-gray-900">What is a Key?</h2>
      <p className="mb-2 text-gray-600">
        A <strong>key</strong> uniquely identifies each row. It answers the question:{' '}
        <em>"Which specific row are you talking about?"</em>
      </p>
      <p className="mb-6 text-gray-600">
        Click the column headers below to select which columns you think form the key.
        Then check your answer.
      </p>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr>
              {KEY_TABLE.headers.map((h, ci) => {
                const isSelected = selectedCols.includes(ci)
                return (
                  <th
                    key={h}
                    onClick={() => toggleCol(ci)}
                    className={`cursor-pointer border border-gray-200 px-4 py-3 text-left font-semibold transition-all ${
                      isSelected
                        ? 'bg-blue-100 text-blue-800 ring-2 ring-blue-400 ring-inset'
                        : 'bg-gray-100 text-gray-700 hover:bg-blue-50'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      {isSelected && <Key className="h-4 w-4 text-blue-600" />}
                      {h}
                    </div>
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody>
            {KEY_TABLE.rows.map((row, ri) => {
              const isDuplicate = uniqueStatus?.duplicateRows?.has(ri)
              return (
                <tr key={ri}>
                  {row.map((cell, ci) => {
                    const isSelected = selectedCols.includes(ci)
                    return (
                      <td
                        key={ci}
                        className={`border border-gray-200 px-4 py-2.5 transition-colors ${
                          isSelected && isDuplicate
                            ? 'bg-red-50 text-red-700 font-medium'
                            : isSelected
                              ? 'bg-blue-50 text-blue-800 font-medium'
                              : ri % 2 === 0
                                ? 'bg-white text-gray-600'
                                : 'bg-gray-50 text-gray-600'
                        }`}
                      >
                        {cell}
                        {isSelected && isDuplicate && ci === selectedCols[selectedCols.length - 1] && (
                          <span className="ml-2 text-xs text-red-500 font-normal">duplicate</span>
                        )}
                      </td>
                    )
                  })}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {/* Unique status indicator */}
      {uniqueStatus && (
        <div className={`mt-3 text-sm font-medium ${uniqueStatus.hasDuplicate ? 'text-red-600' : 'text-emerald-600'}`}>
          {uniqueStatus.hasDuplicate
            ? 'These columns have duplicates -- not unique per row.'
            : 'Every combination is unique -- this could be a key!'}
        </div>
      )}

      <div className="mt-4 flex items-center gap-3">
        <button
          onClick={checkKey}
          disabled={selectedCols.length === 0}
          className="rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-medium text-white shadow-sm transition-colors hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
        >
          Check My Answer
        </button>
        {selectedCols.length > 0 && (
          <button
            onClick={() => { setSelectedCols([]); setFeedback(null) }}
            className="text-sm text-gray-500 hover:text-gray-700"
          >
            Reset
          </button>
        )}
      </div>

      <AnimatePresence mode="wait">
        {feedback && (
          <motion.div
            key={feedback.type}
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className={`mt-4 rounded-lg border p-4 ${
              feedback.type === 'success'
                ? 'border-emerald-200 bg-emerald-50'
                : 'border-red-200 bg-red-50'
            }`}
          >
            <p className={`text-sm font-medium ${
              feedback.type === 'success' ? 'text-emerald-800' : 'text-red-800'
            }`}>
              {feedback.text}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.section>
  )
}

/* ─── Section 2: Why Keys Matter ─── */
function SectionWhyKeys() {
  const [searching, setSearching] = useState(false)
  const [searchKey, setSearchKey] = useState(null)
  const [highlightedRow, setHighlightedRow] = useState(null)

  const handleSearch = () => {
    setSearching(true)
    setHighlightedRow(null)
    setSearchKey({ offer: 'offer_123', date: '2024-01-15' })

    // Animate through rows
    let row = 0
    const interval = setInterval(() => {
      setHighlightedRow(row)
      if (
        KEY_TABLE.rows[row][0] === 'offer_123' &&
        KEY_TABLE.rows[row][1] === '2024-01-15'
      ) {
        clearInterval(interval)
        setTimeout(() => setSearching(false), 200)
      } else {
        row++
        if (row >= KEY_TABLE.rows.length) {
          clearInterval(interval)
          setSearching(false)
        }
      }
    }, 400)
  }

  const foundRow = highlightedRow !== null &&
    KEY_TABLE.rows[highlightedRow]?.[0] === 'offer_123' &&
    KEY_TABLE.rows[highlightedRow]?.[1] === '2024-01-15'

  return (
    <motion.section
      variants={sectionVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.2 }}
      className="rounded-2xl border border-gray-200 bg-white p-6 sm:p-8 shadow-sm"
    >
      <h2 className="mb-2 text-2xl font-bold text-gray-900">Why Keys Matter</h2>
      <p className="mb-4 text-gray-600">
        A key is how you say <em>"give me the data for THIS specific thing."</em> It's like a
        home address -- just the street name isn't enough, you need the full address to find the
        right house.
      </p>

      {/* Search demo */}
      <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 p-4">
        <p className="mb-3 text-sm text-blue-800">
          <strong>Looking for:</strong> Clicks for <code className="rounded bg-blue-100 px-1.5 py-0.5 font-mono text-xs">offer_123</code> on{' '}
          <code className="rounded bg-blue-100 px-1.5 py-0.5 font-mono text-xs">2024-01-15</code>
        </p>
        <button
          onClick={handleSearch}
          disabled={searching}
          className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-blue-700 disabled:bg-blue-400"
        >
          {searching ? 'Searching...' : 'Search by Key'}
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr>
              {KEY_TABLE.headers.map((h) => (
                <th key={h} className="border border-gray-200 bg-gray-100 px-4 py-2.5 text-left font-semibold text-gray-700">
                  {h}
                </th>
              ))}
              <th className="border border-gray-200 bg-gray-100 px-4 py-2.5 text-left font-semibold text-gray-400 w-20">
                Match?
              </th>
            </tr>
          </thead>
          <tbody>
            {KEY_TABLE.rows.map((row, ri) => {
              const isHighlighted = highlightedRow === ri
              const isMatch =
                row[0] === 'offer_123' && row[1] === '2024-01-15' && isHighlighted && !searching
              const isScanning = isHighlighted && searching
              const wasScanned = highlightedRow !== null && ri < highlightedRow

              return (
                <motion.tr
                  key={ri}
                  animate={
                    isMatch
                      ? { backgroundColor: 'rgba(16, 185, 129, 0.15)' }
                      : isScanning
                        ? { backgroundColor: 'rgba(59, 130, 246, 0.1)' }
                        : { backgroundColor: 'rgba(0,0,0,0)' }
                  }
                  transition={{ duration: 0.2 }}
                >
                  {row.map((cell, ci) => (
                    <td
                      key={ci}
                      className={`border border-gray-200 px-4 py-2.5 transition-colors ${
                        isMatch
                          ? 'text-emerald-800 font-semibold'
                          : 'text-gray-600'
                      }`}
                    >
                      {cell}
                    </td>
                  ))}
                  <td className="border border-gray-200 px-4 py-2.5 text-center">
                    {isMatch && (
                      <motion.span
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        className="inline-block text-emerald-600 font-bold"
                      >
                        <CheckCircle className="h-5 w-5 inline" />
                      </motion.span>
                    )}
                    {wasScanned && !isMatch && (
                      <span className="text-gray-300 text-xs">--</span>
                    )}
                  </td>
                </motion.tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {foundRow && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50 p-4"
        >
          <p className="text-sm font-medium text-emerald-800">
            Found it! The key <code className="rounded bg-emerald-100 px-1.5 py-0.5 font-mono text-xs">offer_123 + 2024-01-15</code> points
            to exactly one row: <strong>1,250 clicks</strong>. Without both parts of the key, you'd match
            multiple rows.
          </p>
        </motion.div>
      )}

      {/* Analogy */}
      <div className="mt-6 rounded-lg border border-amber-200 bg-amber-50 p-4">
        <p className="text-sm text-amber-800">
          <strong>Analogy:</strong> A key is like a home address. "Main Street" (Offer ID) isn't enough --
          there are many houses on Main Street. "Number 42" (Date) isn't enough either -- many streets have a #42.
          But "42 Main Street" (Offer ID + Date) takes you to exactly one house.
        </p>
      </div>
    </motion.section>
  )
}

/* ─── Section 3: How Tables Relate ─── */
function SectionRelationships() {
  const [animStep, setAnimStep] = useState(-1)
  const [isAnimating, setIsAnimating] = useState(false)

  const connections = [
    { leftRow: 0, rightRow: 0, label: 'offer_123 + Jan 15' },
    { leftRow: 1, rightRow: 1, label: 'offer_456 + Jan 15' },
    { leftRow: 2, rightRow: 2, label: 'offer_123 + Jan 16' },
    { leftRow: 3, rightRow: 3, label: 'offer_456 + Jan 16' },
  ]

  const startAnimation = () => {
    setIsAnimating(true)
    setAnimStep(-1)
    let step = 0
    const interval = setInterval(() => {
      setAnimStep(step)
      step++
      if (step >= connections.length) {
        clearInterval(interval)
        setTimeout(() => setIsAnimating(false), 300)
      }
    }, 700)
  }

  const ROW_HEIGHT = 41 // px per data row
  const HEADER_HEIGHT = 41
  const TABLE_TOP_OFFSET = 0

  return (
    <motion.section
      variants={sectionVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.2 }}
      className="rounded-2xl border border-gray-200 bg-white p-6 sm:p-8 shadow-sm"
    >
      <h2 className="mb-2 text-2xl font-bold text-gray-900">How Tables Relate</h2>
      <p className="mb-2 text-gray-600">
        This is <strong>the critical concept</strong> for the whole course. When two tables share the
        same key columns, you can <strong>match rows between them</strong>.
      </p>
      <p className="mb-6 text-gray-600">
        Click the button to see how rows in the Clicks table connect to rows in the Conversions table
        through their shared key (Offer ID + Date).
      </p>

      <button
        onClick={startAnimation}
        disabled={isAnimating}
        className="mb-6 rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-medium text-white shadow-sm transition-colors hover:bg-blue-700 disabled:bg-blue-400"
      >
        {isAnimating ? 'Connecting...' : animStep >= 0 ? 'Show Connections Again' : 'Show How Tables Connect'}
      </button>

      {/* Two tables side by side with connection lines */}
      <div className="relative">
        <div className="grid gap-8 md:grid-cols-2 lg:gap-16">
          {/* Clicks table */}
          <div>
            <div className="mb-2 flex items-center gap-2">
              <div className="h-3 w-3 rounded-full bg-blue-400" />
              <h3 className="text-sm font-bold text-blue-700 uppercase tracking-wide">Clicks Table</h3>
            </div>
            <div className="overflow-hidden rounded-lg border border-blue-200">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-blue-50">
                    {CLICKS_TABLE.headers.map((h) => (
                      <th key={h} className="px-3 py-2.5 text-left text-xs font-semibold text-blue-800">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {CLICKS_TABLE.rows.map((row, ri) => {
                    const isConnected = animStep >= ri
                    return (
                      <tr
                        key={ri}
                        className={`transition-colors duration-300 ${
                          isConnected ? 'bg-blue-50' : 'bg-white'
                        }`}
                      >
                        {row.map((cell, ci) => (
                          <td
                            key={ci}
                            className={`border-t border-blue-100 px-3 py-2.5 ${
                              isConnected && ci < 2
                                ? 'font-semibold text-blue-700'
                                : 'text-gray-600'
                            }`}
                          >
                            {cell}
                          </td>
                        ))}
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Conversions table */}
          <div>
            <div className="mb-2 flex items-center gap-2">
              <div className="h-3 w-3 rounded-full bg-emerald-400" />
              <h3 className="text-sm font-bold text-emerald-700 uppercase tracking-wide">Conversions Table</h3>
            </div>
            <div className="overflow-hidden rounded-lg border border-emerald-200">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-emerald-50">
                    {CONV_TABLE.headers.map((h) => (
                      <th key={h} className="px-3 py-2.5 text-left text-xs font-semibold text-emerald-800">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {CONV_TABLE.rows.map((row, ri) => {
                    const isConnected = animStep >= ri
                    return (
                      <tr
                        key={ri}
                        className={`transition-colors duration-300 ${
                          isConnected ? 'bg-emerald-50' : 'bg-white'
                        }`}
                      >
                        {row.map((cell, ci) => (
                          <td
                            key={ci}
                            className={`border-t border-emerald-100 px-3 py-2.5 ${
                              isConnected && ci < 2
                                ? 'font-semibold text-emerald-700'
                                : 'text-gray-600'
                            }`}
                          >
                            {cell}
                          </td>
                        ))}
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Connection indicators (shown between/below tables on all screen sizes) */}
        <AnimatePresence>
          {animStep >= 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="mt-4 space-y-2"
            >
              {connections.map((conn, i) => (
                i <= animStep && (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4 }}
                    className="flex items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 px-4 py-2"
                  >
                    <Link2 className="h-4 w-4 shrink-0 text-blue-500" />
                    <span className="text-sm text-gray-700">
                      <code className="rounded bg-purple-100 px-1.5 py-0.5 font-mono text-xs font-semibold text-purple-700">
                        {conn.label}
                      </code>
                      <span className="mx-2 text-gray-400">&rarr;</span>
                      <span className="text-xs text-gray-500">
                        {CLICKS_TABLE.rows[conn.leftRow][2]} clicks + {CONV_TABLE.rows[conn.rightRow][2]} conversions
                      </span>
                    </span>
                  </motion.div>
                )
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* JOIN explanation */}
      {animStep >= connections.length - 1 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mt-6 space-y-4"
        >
          <div className="rounded-lg border border-purple-200 bg-purple-50 p-4">
            <h4 className="mb-2 font-semibold text-purple-800">This is called a JOIN</h4>
            <p className="text-sm text-purple-700">
              Combining two tables using their shared key. Because both tables have
              Date + Offer ID, we can match every row of clicks with its corresponding
              conversions. This is the foundation of everything we'll build in this course.
            </p>
          </div>

          {/* Combined result preview */}
          <div className="overflow-x-auto">
            <div className="mb-2 text-sm font-bold text-gray-700 uppercase tracking-wide">
              Combined Result
            </div>
            <div className="rounded-lg border-2 border-purple-200">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-purple-50">
                    {['Date', 'Offer ID', 'Clicks', 'Conversions', 'Conv. Rate'].map((h) => (
                      <th key={h} className="px-3 py-2.5 text-left text-xs font-semibold text-purple-800">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {CLICKS_TABLE.rows.map((row, ri) => {
                    const clicks = parseInt(row[2].replace(',', ''))
                    const convs = parseInt(CONV_TABLE.rows[ri][2])
                    const rate = ((convs / clicks) * 100).toFixed(1)
                    return (
                      <motion.tr
                        key={ri}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.1 * ri }}
                        className={ri % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
                      >
                        <td className="border-t border-purple-100 px-3 py-2.5 text-gray-600">{row[0]}</td>
                        <td className="border-t border-purple-100 px-3 py-2.5 text-gray-600">{row[1]}</td>
                        <td className="border-t border-purple-100 px-3 py-2.5 font-medium text-blue-700">{row[2]}</td>
                        <td className="border-t border-purple-100 px-3 py-2.5 font-medium text-emerald-700">{CONV_TABLE.rows[ri][2]}</td>
                        <td className="border-t border-purple-100 px-3 py-2.5 font-medium text-purple-700">{rate}%</td>
                      </motion.tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </motion.div>
      )}
    </motion.section>
  )
}

/* ─── Main Module Component ─── */
export default function Module3() {
  return (
    <ModuleLayout moduleId={3}>
      <div className="space-y-10">
        <SectionWhatIsKey />
        <SectionWhyKeys />
        <SectionRelationships />
        <Quiz moduleId={3} questions={QUIZ} />
      </div>
    </ModuleLayout>
  )
}
